import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import {
  EventNote as EventIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import queryClient from "../../../base/QueryClient";
import { getRecordAbsence, deleteRecordAbsence } from "../../../api/services/recordAbsence";
import RecordAbsencesModal from "./RecordAbsencesModal";
import RecordAbsenceEditModal from "./RecordAbsenceEditModal";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import DataTable from "../../Table/DataTable";
import { formatDate, formatTime } from "../../../utils/time";

const RecordAbsences = () => {
  const theme = useTheme();
  const { id: clientId } = useParams();
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const { data: absenceData, refetch: refetchRecord } = useQuery(
    [QK.record, clientId],
    () => getRecordAbsence(clientId),
    {
      enabled: !!clientId,
    }
  );

  const deleteRecordMutation = useMutation(deleteRecordAbsence, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.record, clientId]);
      enqueueSnackbar("Record Absence deleted successfully", {
        variant: "success",
      });
      refetchRecord();
      setShowConfirmModal(false);
    },
    onError: (error) => {
      enqueueSnackbar("Failed to delete Record Absence", { variant: "error" });
    },
  });

  const handleRecordShow = () => setShowRecordModal(true);
  const handleRecordClose = () => setShowRecordModal(false);

  const handleRecordDelete = (id) => {
    setSelectedRecord(id);
    setShowConfirmModal(true);
  };

  const handleRecordDeleteConfirm = () => {
    if (selectedRecord) {
      deleteRecordMutation.mutate(selectedRecord);
    }
  };

  const handleRecordEdit = (id) => {
    setSelectedRecord(id);
    setShowEditModal(true);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setSelectedRecord(null);
  };

  const absenceList = absenceData || [];

  const headers = [
    { key: "start_date", label: "Start Date & Time" },
    { key: "end_date", label: "End Date & Time" },
    { key: "absence_type", label: "Absence Type" },
    { key: "reason", label: "Reason" },
    { key: "note_message", label: "Notes" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "start_date" && item.start_date) {
      return (
        <span>
          {formatDate(item.start_date)}
          {item.start_time && ` ${formatTime(item.start_time)}`}
        </span>
      );
    }
    if (header.key === "end_date" && item.end_date) {
      return (
        <span>
          {formatDate(item.end_date)}
          {item.end_time && ` ${formatTime(item.end_time)}`}
        </span>
      );
    }
    if (header.key === "absence_type") {
      return (
        <Chip
          label={item.absence_type || "N/A"}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 500,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            color: theme.palette.info.main,
          }}
        />
      );
    }
    if (header.key === "note_message") {
      return (
        <Tooltip title={item.note_message || ""}>
          <span
            style={{
              display: "block",
              maxWidth: 120,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.note_message || "-"}
          </span>
        </Tooltip>
      );
    }
    return item[header.key] || "-";
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                bgcolor: alpha(theme.palette.info.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EventIcon sx={{ color: theme.palette.info.main, fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1A1A2E", fontSize: "0.85rem" }}>
              Record Absences
            </Typography>
            <Chip
              label={absenceList.length}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.55rem",
                fontWeight: 500,
                bgcolor: "#F2F4F7",
                color: "#667085",
              }}
            />
          </Box>
          <IconButton
            onClick={handleRecordShow}
            size="small"
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#2469F0",
              color: "#fff",
              "&:hover": {
                bgcolor: "#1A5BD4",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Divider sx={{ opacity: 0.4 }} />

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={absenceList}
            headers={headers}
            onUpdate={handleRecordEdit}
            onDelete={handleRecordDelete}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={false}
          />
        </CardContent>
      </Card>

      <RecordAbsencesModal
        show={showRecordModal}
        onHide={handleRecordClose}
        refetchRecord={refetchRecord}
      />

      <RecordAbsenceEditModal
        show={showEditModal}
        handleClose={handleEditClose}
        refetchRecord={refetchRecord}
        selectedRecord={selectedRecord}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleRecordDeleteConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this absence record?"
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default RecordAbsences;